import React, { useState, useEffect, useCallback } from 'react';
import { roomsAPI, apiClient, productsAPI, publicAPI } from './services/api';
import PaymentModal from './PaymentModal';
import ReservationCalendar from './ReservationCalendar';
import SalesAgreement from './SalesAgreement';
import LazyImage from './LazyImage';
import './ReservationPage.css';

interface ReservationPageProps {
  checkIn?: string;
  checkOut?: string;
  adults?: number;
  children?: number;
  childrenAges?: number[];
  autoSearch?: boolean;
}

const isValidFullName = (n: string) =>
  n.trim().split(/\s+/).filter(w => w.length >= 2).length >= 2;

const ReservationPage: React.FC<ReservationPageProps> = ({ 
  checkIn: initialCheckIn = '', 
  checkOut: initialCheckOut = '', 
  adults: initialAdults = 2,
  children: initialChildren = 0,
  childrenAges: initialChildrenAges,
  autoSearch = false
}) => {
  const [checkInDate, setCheckInDate] = useState(initialCheckIn);
  const [checkOutDate, setCheckOutDate] = useState(initialCheckOut);
  const [adultsCount, setAdultsCount] = useState(initialAdults);
  const [childrenCount, setChildrenCount] = useState(initialChildren);
  const [childrenAges, setChildrenAges] = useState<number[]>(
    initialChildrenAges && initialChildrenAges.length > 0
      ? initialChildrenAges
      : initialChildren > 0 ? Array(initialChildren).fill(7) : []
  );
  const [availableRooms, setAvailableRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [blockedDates, setBlockedDates] = useState<{ date: string; reason: string; affects: string[] }[]>([]);
  const [searched, setSearched] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [hasAutoSearched, setHasAutoSearched] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<Record<string, number>>({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  
  // Payment Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [createdBookingId, setCreatedBookingId] = useState<string | number>('');
  
  // Discount Code State
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState<any>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountMessage, setDiscountMessage] = useState({ text: '', type: '' });
  
  // Sales Agreement State
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [guestGroupType, setGuestGroupType] = useState<'' | 'mixed' | 'male_only'>();
  const [guestName, setGuestName] = useState('');
  
  // Kahvaltı için kişi sayıları
  const [breakfastAdults, setBreakfastAdults] = useState(0);
  const [breakfastChildren7_12, setBreakfastChildren7_12] = useState(0);
  const [breakfastChildren0_6, setBreakfastChildren0_6] = useState(0);
  
  // Akşam yemeği için kişi sayıları
  const [dinnerAdults, setDinnerAdults] = useState(0);
  const [dinnerChildren7_12, setDinnerChildren7_12] = useState(0);
  const [dinnerChildren0_6, setDinnerChildren0_6] = useState(0);
  
  // Extra Bed Warning Modal State
  const [showExtraBedWarning, setShowExtraBedWarning] = useState(false);
  const [pendingRoom, setPendingRoom] = useState<any>(null);
  
  // Meal Add-ons State
  const [includeBreakfast, setIncludeBreakfast] = useState(false);
  const [includeDinner, setIncludeDinner] = useState(false);
  const [dinnerClosedDays, setDinnerClosedDays] = useState<number[]>([1]);
  // Pazartesi/Pazar uyarı onayları
  const [sundayMondayAgreed, setSundayMondayAgreed] = useState(false);
  const [mondayAgreed, setMondayAgreed] = useState(false);
  const [mealPricing, setMealPricing] = useState({
    breakfastAdult: 0, // API'den yüklenene kadar 0
    breakfastChild7_12: 0,
    breakfastChild0_6: 0,
    dinnerAdult: 0,
    dinnerChild7_12: 0,
    dinnerChild0_6: 0
  });
  const [mealPricesLoaded, setMealPricesLoaded] = useState(false);
  const [mealPriceError, setMealPriceError] = useState(false);

  // Fatura bilgileri
  const [invoiceType, setInvoiceType] = useState<'individual' | 'corporate'>('individual');
  const [invoiceName, setInvoiceName] = useState('');
  const [invoiceTckn, setInvoiceTckn] = useState('');
  const [invoiceTaxNumber, setInvoiceTaxNumber] = useState('');
  const [invoiceTaxOffice, setInvoiceTaxOffice] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoiceDistrict, setInvoiceDistrict] = useState('');
  const [invoiceCity, setInvoiceCity] = useState('');
  const [invoicePostalCode, setInvoicePostalCode] = useState('');

  // Yemek fiyatlarını API'den çek - FALLBACK YOK! API başarısız olursa rezervasyon alamaz
  useEffect(() => {
    const fetchMealPricing = async () => {
      try {
        const [lodgingBreakfastRes, dinnerRes, dinnerPublicRes] = await Promise.all([
          productsAPI.getPublicSetting('lodging_breakfast'),
          productsAPI.getByType('dinner'),
          productsAPI.getPublicSetting('dinner_public').catch(() => null)
        ]);

        const lodgingBreakfastValue = lodgingBreakfastRes.data?.data?.value;
        const dinnerContent = dinnerRes.data?.data?.content;

        const newPricing = {
          breakfastAdult: lodgingBreakfastValue?.price_rules?.adult || 0,
          breakfastChild7_12: lodgingBreakfastValue?.price_rules?.child_7_12 || 0,
          breakfastChild0_6: lodgingBreakfastValue?.price_rules?.child_0_6 || 0,
          dinnerAdult: dinnerContent?.price_rules?.adult || 0,
          dinnerChild7_12: dinnerContent?.price_rules?.child_7_12 || 0,
          dinnerChild0_6: dinnerContent?.price_rules?.child_0_6 || 0
        };

        // Kritik fiyatlar 0 ise hata ver
        if (!newPricing.dinnerAdult || !newPricing.breakfastAdult) {
          throw new Error('Fiyat bilgileri eksik (dinner: ' + newPricing.dinnerAdult + ', breakfast: ' + newPricing.breakfastAdult + ')');
        }

        setMealPricing(newPricing);
        setMealPricesLoaded(true);
        setMealPriceError(false);

        const dinnerPublicValue = dinnerPublicRes?.data?.data?.value;
        if (dinnerPublicValue) {
          const normalizedDays: number[] = Array.isArray(dinnerPublicValue.closed_days)
            ? Array.from(new Set<number>(dinnerPublicValue.closed_days
                .map((d: any) => Number(d))
                .filter((d: number) => Number.isInteger(d) && d >= 0 && d <= 6)))
                .sort((a, b) => a - b)
            : [1];
          setDinnerClosedDays(normalizedDays.length > 0 ? normalizedDays : [1]);
        }

        console.log('✅ Fiyatlar API\'den yüklendi:', newPricing);
      } catch (error) {
        console.error('❌ HATA: Yemek fiyatları yüklenemedi! Rezervasyon sistemi devre dışı.', error);
        setMealPriceError(true);
        setMealPricesLoaded(true);
        // Fiyatlar yüklenemezse rezervasyon ALMAmalı - güvenlik önlemi
      }
    };

    fetchMealPricing();
  }, []);

  // LocalStorage'dan rezervasyon bilgilerini oku (Multi-Room Calendar'dan gelen)
  useEffect(() => {
    const savedData = localStorage.getItem('reservationData');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        console.log('📋 LocalStorage\'dan rezervasyon bilgileri yüklendi:', data);
        
        // Tarihleri set et
        setCheckInDate(data.checkInDate);
        setCheckOutDate(data.checkOutDate);
        
        // Misafir bilgilerini set et
        setAdultsCount(data.adults);
        setChildrenCount(data.children);
        setChildrenAges(data.childAges || []);
        
        // Oda ID'sini sakla (otomatik arama sonrası seçmek için)
        if (data.roomId) {
          sessionStorage.setItem('preSelectedRoomId', data.roomId);
        }
        
        // LocalStorage'ı temizle
        localStorage.removeItem('reservationData');
        
        // Otomatik arama yap
        setHasAutoSearched(false); // Trigger otomatik arama
      } catch (error) {
        console.error('LocalStorage parse hatası:', error);
        localStorage.removeItem('reservationData');
      }
    }
  }, []);

  const updateChildrenCount = (count: number) => {
    setChildrenCount(count);
    setChildrenAges(prev => {
      if (count > prev.length) {
        return [...prev, ...Array(count - prev.length).fill(7)];
      } else {
        return prev.slice(0, count);
      }
    });
    // Misafir sayısı değişti - eski fiyatlar geçersiz, tekrar arama gerekli
    if (searched) {
      setAvailableRooms([]);
      setError('Misafir sayısı değişti. Lütfen tekrar arama yapınız.');
    }
  };

  const updateAdultsCount = (count: number) => {
    setAdultsCount(count);
    // Misafir sayısı değişti - eski fiyatlar geçersiz, tekrar arama gerekli
    if (searched) {
      setAvailableRooms([]);
      setError('Misafir sayısı değişti. Lütfen tekrar arama yapınız.');
    }
  };

  const updateChildAge = (index: number, age: number) => {
    setChildrenAges(prev => {
      const newAges = [...prev];
      newAges[index] = age;
      return newAges;
    });
    // Çocuk yaşı değişti - fiyatlar değişebilir, tekrar arama gerekli
    if (searched) {
      setAvailableRooms([]);
      setError('Çocuk yaşı değişti. Lütfen tekrar arama yapınız.');
    }
  };

  // Kapalı tarihleri yükle
  useEffect(() => {
    publicAPI.getBlockedDates()
      .then(res => { if (res.data.success) setBlockedDates(res.data.data); })
      .catch(() => {});
  }, []);

  const searchAvailability = useCallback(async () => {
    if (!checkInDate || !checkOutDate) {
      setError('Lütfen giriş ve çıkış tarihlerini seçiniz');
      return;
    }

    // Kapalı tarih aralığı kontrolü
    {
      const ci = new Date(checkInDate);
      const co = new Date(checkOutDate);
      const blocked = blockedDates.find(entry => {
        if (!entry.affects.includes('all') && !entry.affects.includes('rooms')) return false;
        const d = new Date(entry.date);
        return d >= ci && d < co;
      });
      if (blocked) {
        const reason = blocked.reason ? ` (${blocked.reason})` : '';
        setError(`Seçilen tarih aralığında${reason} oda rezervasyonu yapılamamaktadır (${blocked.date}). Lütfen farklı tarih seçiniz.`);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSearched(true);
    
    try {
      const response = await roomsAPI.getAvailability({
        checkIn: checkInDate,
        checkOut: checkOutDate,
        adults: Number(adultsCount),
        children: Number(childrenCount),
        childrenAges: childrenAges.join(',')
      });

      if (response.data.success && response.data.data && response.data.data.length > 0) {
        setAvailableRooms(response.data.data);
        // Her oda için başlangıç index'i 0 olarak ayarla
        const initialIndexes: Record<string, number> = {};
        response.data.data.forEach((room: any) => {
          initialIndexes[room.roomTypeId || room.id] = 0;
        });
        setCurrentImageIndex(initialIndexes);
        
        // Eğer pre-selected room varsa otomatik seç
        const preSelectedRoomId = sessionStorage.getItem('preSelectedRoomId');
        if (preSelectedRoomId) {
          const matchedRoom = response.data.data.find((room: any) => 
            room.roomTypeId === preSelectedRoomId || room.id === preSelectedRoomId
          );
          if (matchedRoom) {
            console.log('🎯 Takvimden seçilen oda otomatik seçildi:', matchedRoom);
            
            // Otomatik olarak rezervasyon formunu aç
            setTimeout(() => {
              setSelectedRoom(matchedRoom);
              setShowBookingForm(true);
              
              // Meal sayılarını otomatik ayarla (yetişkin ve çocuk sayısına göre)
              const totalGuests = adultsCount + childrenCount;
              setBreakfastAdults(adultsCount);
              setDinnerAdults(adultsCount);
              
              // Çocukları yaşa göre böl
              const children7_12 = childrenAges.filter(age => age >= 7 && age <= 12).length;
              const children0_6 = childrenAges.filter(age => age >= 0 && age < 7).length;
              
              setBreakfastChildren7_12(children7_12);
              setBreakfastChildren0_6(children0_6);
              setDinnerChildren7_12(children7_12);
              setDinnerChildren0_6(children0_6);
              
              console.log('🍽️ Meal sayıları otomatik ayarlandı:', {
                adults: adultsCount,
                children7_12,
                children0_6
              });
              
              // SessionStorage'ı temizle
              sessionStorage.removeItem('preSelectedRoomId');
              
              // Forma scroll
              setTimeout(() => {
                const formElement = document.querySelector('.booking-form-section');
                if (formElement) {
                  formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }, 500);
          } else {
            console.warn('⚠️ Pre-selected room bulunamadı, manuel seçim gerekli');
            sessionStorage.removeItem('preSelectedRoomId');
          }
        }
      } else {
        // API'den gelen mesajı kullan (kapasite mi? tarih mi dolu?), yoksa genel mesaj göster
        const apiMsg = response.data.message;
        setError(apiMsg || 'Seçilen tarihler için müsait oda bulunamadı.');
        setAvailableRooms([]);
      }
    } catch (err: any) {
      console.error('Oda müsaitliği hatası:', err);
      setError('Oda müsaitliği sorgulanırken bir hata oluştu. Lütfen tekrar deneyiniz.');
      setAvailableRooms([]);
    } finally {
      setLoading(false);
    }
  }, [checkInDate, checkOutDate, adultsCount, childrenCount, childrenAges]);

  // Anasayfadan gelindiyse otomatik arama yap
  useEffect(() => {
    if (autoSearch && checkInDate && checkOutDate && !hasAutoSearched) {
      setHasAutoSearched(true);
      searchAvailability();
    }
  }, [autoSearch, checkInDate, checkOutDate, hasAutoSearched, searchAvailability]);

  // Anasayfadan autoSearch ile gelindiyse sonuçlar yüklenince otomatik scroll
  useEffect(() => {
    if (!autoSearch || !searched || loading) return;
    const el = document.querySelector('.results-section, .no-results');
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [autoSearch, searched, loading, availableRooms]);

  const getImageUrl = (url: string): string => {
    if (url.startsWith('http')) return url; // External URL
    return url; // /uploads/rooms/xxx.jpg -> nginx proxy ile backend'e gider
  };

  // Resim carousel fonksiyonları
  const nextImage = (roomId: string, totalImages: number) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) + 1) % totalImages
    }));
  };

  const prevImage = (roomId: string, totalImages: number) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  // Lightbox fonksiyonları
  const openLightbox = (roomId: string) => {
    setActiveRoomId(roomId);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Scroll'u engelle
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setActiveRoomId(null);
    document.body.style.overflow = 'auto'; // Scroll'u geri aç
  };

  const handleRoomSelect = (room: any) => {
    // Check if extra bed is needed - totalGuests > maxAdults means extra bed required
    const totalGuests = Number(adultsCount) + Number(childrenCount);
    const standardCap = room.standardCapacity || room.maxAdults || room.maxOccupancy || 3;
    
    console.log('🔍 Capacity Check:', { 
      adultsCount, 
      childrenCount, 
      totalGuests, 
      standardCapacity: standardCap,
      maxOccupancy: room.maxOccupancy,
      needsExtraBed: totalGuests > standardCap 
    });
    
    if (totalGuests > standardCap) {
      // Show extra bed warning modal
      setPendingRoom(room);
      setShowExtraBedWarning(true);
    } else {
      // Proceed directly
      proceedWithBooking(room);
    }
  };
  
  const proceedWithBooking = (room: any) => {
    setSelectedRoom(room);
    setShowBookingForm(true);
    // Reset discount when room changes
    setDiscountApplied(null);
    setDiscountCode('');
    setDiscountMessage({ text: '', type: '' });
    // Reset agreement
    setAgreementAccepted(false);
    setGuestName('');
    
    // Varsayılan olarak tüm misafirleri set et
    const children0_6 = childrenAges.filter(a => a >= 0 && a <= 6).length;
    const children7_12 = childrenAges.filter(a => a >= 7 && a <= 12).length;
    const teenAdults = childrenAges.filter(a => a >= 13).length;
    const effectiveAdults = adultsCount + teenAdults;
    
    setBreakfastAdults(effectiveAdults);
    setBreakfastChildren7_12(children7_12);
    setBreakfastChildren0_6(children0_6);
    
    setDinnerAdults(effectiveAdults);
    setDinnerChildren7_12(children7_12);
    setDinnerChildren0_6(children0_6);
    
    // Reset meal add-ons
    setIncludeBreakfast(false);
    setIncludeDinner(false);
  };

  // Calculate meal costs based on guests and ages
  // Pazartesi hariç tutulan gün sayılarını hesapla
  const getMondayExclusions = (checkIn: string, checkOut: string) => {
    if (!checkIn || !checkOut) return { breakfasts: 0, dinners: 0 };
    let breakfasts = 0, dinners = 0;
    const end = new Date(checkOut + 'T12:00:00');
    for (let d = new Date(checkIn + 'T12:00:00'); d < end; d.setDate(d.getDate() + 1)) {
      if (dinnerClosedDays.includes(d.getDay())) dinners++;
      const morning = new Date(d);
      morning.setDate(morning.getDate() + 1);
      if (dinnerClosedDays.includes(morning.getDay())) breakfasts++;
    }
    return { breakfasts, dinners };
  };

const calculateMealCosts = () => {
    if (!selectedRoom) return { breakfast: 0, dinner: 0, total: 0, excludedBreakfasts: 0, excludedDinners: 0, billableBreakfastNights: 0, billableDinnerNights: 0 };
    
    const nights = selectedRoom.nights || 1;
    const excl = getMondayExclusions(checkInDate, checkOutDate);
    const billableBreakfastNights = Math.max(0, nights - excl.breakfasts);
    const billableDinnerNights = Math.max(0, nights - excl.dinners);
    
    let breakfastCost = 0;
    let dinnerCost = 0;

    // Seçilen kişi sayılarına göre hesapla
    if (includeBreakfast) {
      breakfastCost = billableBreakfastNights * (
        breakfastAdults * mealPricing.breakfastAdult +
        breakfastChildren7_12 * mealPricing.breakfastChild7_12 +
        breakfastChildren0_6 * mealPricing.breakfastChild0_6
      );
    }

    if (includeDinner) {
      dinnerCost = billableDinnerNights * (
        dinnerAdults * mealPricing.dinnerAdult +
        dinnerChildren7_12 * mealPricing.dinnerChild7_12 +
        dinnerChildren0_6 * mealPricing.dinnerChild0_6
      );
    }

    return {
      breakfast: breakfastCost,
      dinner: dinnerCost,
      total: breakfastCost + dinnerCost,
      excludedBreakfasts: excl.breakfasts,
      excludedDinners: excl.dinners,
      billableBreakfastNights,
      billableDinnerNights
    };
  };

  const handleApplyDiscount = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) {
      setDiscountMessage({ text: 'Lütfen bir indirim kodu girin', type: 'warning' });
      return;
    }


    // Form alanlarından email ve telefon bilgilerini al (indirim kodu whitelist kontrolü için)
    const emailInput = document.querySelector('input[name="guestEmail"]') as HTMLInputElement;
    const phoneInput = document.querySelector('input[name="guestPhone"]') as HTMLInputElement;
    const guestEmail = emailInput?.value?.trim() || '';
    const guestPhone = phoneInput?.value?.trim() || '';
    setDiscountLoading(true);
    setDiscountMessage({ text: '', type: '' });

    try {
      const response = await apiClient.post('/modules/hotel/discount-codes/validate', {
        code: code,
        property_id: '9004c629-e08b-428a-8f36-1de7b22af888',
        total_amount: selectedRoom?.price || 0,
        guest_phone: guestPhone,
        guest_email: guestEmail,
        room_type_id: selectedRoom?.roomTypeId
      });

      const data = response.data;

      if (data.valid && data.discount_code) {
        setDiscountApplied({
          ...data.discount_code,
          discount_amount: data.discount_amount,
          original_amount: data.original_amount,
          final_amount: data.final_amount
        });
        setDiscountMessage({ 
          text: `✅ İndirim uygulandı! ${data.discount_amount.toFixed(2)} TL indirim kazandınız.`, 
          type: 'success' 
        });
      } else {
        setDiscountApplied(null);
        setDiscountMessage({ 
          text: data.msg || 'İndirim kodu geçersiz', 
          type: 'error' 
        });
      }
    } catch (error: any) {
      console.error('Discount validation error:', error);
      setDiscountApplied(null);
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || 'İndirim kodu kontrol edilirken hata oluştu';
      setDiscountMessage({ 
        text: errorMsg, 
        type: 'error' 
      });
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountApplied(null);
    setDiscountCode('');
    setDiscountMessage({ text: '', type: '' });
  };

  const handleBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRoom) return;

    if (!guestGroupType) {
      alert('Lütfen misafir grubu cinsiyetini seçin.');
      return;
    }
    if (guestGroupType === 'male_only') {
      alert('Villa Polikne, butik aile işletmesi konseptiyle hizmet vermektedir. Yalnızca erkek misafirlerden oluşan grup veya tekil rezervasyonlar kabul edilmemektedir.');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData(e.target as HTMLFormElement);
      
      // Calculate meal costs
      const mealCosts = calculateMealCosts();
      const roomPrice = discountApplied ? discountApplied.final_amount : (selectedRoom.price || 0);
      const totalWithMeals = roomPrice + mealCosts.total;
      
      const bookingData = {
        source: 'reservation-page',
        roomTypeId: selectedRoom.roomTypeId || selectedRoom.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        adults: adultsCount,
        children: childrenCount,
        childrenAges: childrenAges,
        guestName: guestName,
        guestEmail: formData.get('guestEmail') as string,
        guestPhone: formData.get('guestPhone') as string,
        guestGroupType: guestGroupType || 'mixed',
        specialRequests: formData.get('specialRequests') as string,
        totalPrice: totalWithMeals,
        includeBreakfast: includeBreakfast,
        includeDinner: includeDinner,
        breakfastCost: mealCosts.breakfast,
        dinnerCost: mealCosts.dinner,
        mealDetails: {
          breakfast: includeBreakfast ? {
            adults: breakfastAdults,
            children7_12: breakfastChildren7_12,
            children0_6: breakfastChildren0_6,
            totalPeople: breakfastAdults + breakfastChildren7_12 + breakfastChildren0_6,
            nights: mealCosts.billableBreakfastNights,
            totalCost: mealCosts.breakfast
          } : null,
          dinner: includeDinner ? {
            adults: dinnerAdults,
            children7_12: dinnerChildren7_12,
            children0_6: dinnerChildren0_6,
            totalPeople: dinnerAdults + dinnerChildren7_12 + dinnerChildren0_6,
            nights: mealCosts.billableDinnerNights,
            totalCost: mealCosts.dinner
          } : null
        },
        ...(discountApplied && {
          discountCode: discountApplied.code,
          originalPrice: selectedRoom.price || 0,
          discountAmount: discountApplied.discount_amount || 0
        }),
        invoice_type: invoiceType,
        invoice_name: invoiceName || '',
        ...(invoiceType === 'individual'
          ? { invoice_tckn: invoiceTckn || '', invoice_address: invoiceAddress || '', invoice_district: invoiceDistrict || '', invoice_city: invoiceCity || '', invoice_postal_code: invoicePostalCode || '' }
          : {
              invoice_tax_number: invoiceTaxNumber || '',
              invoice_tax_office: invoiceTaxOffice || '',
              invoice_address: invoiceAddress || '',
              invoice_district: invoiceDistrict || '',
              invoice_city: invoiceCity || '',
              invoice_postal_code: invoicePostalCode || ''
            }
        )
      };

      const response = await roomsAPI.book(bookingData);

      if (response.data.success) {
        const { iframeUrl, reservationId } = response.data.data;
        if (iframeUrl) {
          // window.location.href = iframeUrl;
          setPaymentUrl(iframeUrl);
          setCreatedBookingId(reservationId);
          setShowBookingForm(false);
          setShowPaymentModal(true);
        } else {
          alert('Rezervasyon oluşturuldu: ' + reservationId);
          setShowBookingForm(false);
          setSelectedRoom(null);
        }
      }
    } catch (err: any) {
      alert('Rezervasyon oluşturulamadı: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Helper to get room features with icons
  const getRoomFeatures = (room: any) => {
    const amenityIcons: any = {
      wifi: '📶 WiFi',
      tv: '📺 TV',
      ac: '❄️ Klima',
      minibar: '🍷 Minibar',
      shower: '🚿 Duş',
      hairdryer: '💨 Saç Kurutma',
      balcony: '🏖️ Balkon',
      terrace: '🌅 Teras',
      pool: '🏊 Havuz',
      spa: '💆 Spa',
      gym: '🏋️ Gym',
      parking: '🅿️ Otopark',
      restaurant: '🍽️ Restoran',
      bar: '🍹 Bar',
      breakfast: '🍳 Kahvaltı'
    };

    if (room.amenities && Array.isArray(room.amenities)) {
      return room.amenities.slice(0, 6).map((amenity: string) => 
        amenityIcons[amenity.toLowerCase()] || amenity
      );
    }

    const features = [];
    if (room.maxAdults) features.push(`👥 ${room.maxAdults} Yetişkin`);
    if (room.maxChildren) features.push(`👶 ${room.maxChildren} Çocuk`);
    if (room.viewType) features.push(room.viewType === 'sea' ? '🌊 Deniz Manzara' : '🌳 Bahçe Manzara');
    return features;
  };

  return (
    <div className="reservation-page">
      <div className="container">
        {/* --- Calendar-Based Date Selection --- */}
        <div className="calendar-search-section">
          <ReservationCalendar 
            onDateSelect={(checkIn, checkOut) => {
              setCheckInDate(checkIn);
              setCheckOutDate(checkOut);
              // Tarih değişti - eski fiyatlar geçersiz
              if (searched) {
                setAvailableRooms([]);
                setError('Tarih değişti. Lütfen tekrar arama yapınız.');
              }
            }}
          />
          
          {/* Guest Selection Panel */}
          <div className="guest-selection-panel">
            <h3>Misafir Bilgileri</h3>
            <div className="guest-inputs">
              <div className="form-group">
                <label>YETİŞKİN</label>
                <select 
                  className="form-input"
                  value={adultsCount}
                  onChange={(e) => updateAdultsCount(parseInt(e.target.value))}
                >
                  {[1,2,3].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>ÇOCUK</label>
                <select 
                  className="form-input"
                  value={childrenCount}
                  onChange={(e) => updateChildrenCount(parseInt(e.target.value))}
                >
                  {[0,1,2].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            {childrenCount > 0 && (
              <div className="children-ages-row">
                <label>Çocuk Yaşları:</label>
                <div className="ages-grid">
                  {childrenAges.map((age, index) => (
                    <select 
                      key={index}
                      className="age-select"
                      value={age}
                      onChange={(e) => updateChildAge(index, parseInt(e.target.value))}
                    >
                      {Array.from({length: 18}, (_, i) => i).map(n => (
                        <option key={n} value={n}>{n} yaş</option>
                      ))}
                    </select>
                  ))}
                </div>
              </div>
            )}
            
            <button 
              className="btn-search-rooms" 
              onClick={searchAvailability}
              disabled={loading || !checkInDate || !checkOutDate}
            >
              {loading ? 'ARANIYOR...' : '🔍 MÜSAİT ODALARI ARA'}
            </button>
            
            {checkInDate && checkOutDate && (
              <div className="search-summary">
                <span>{checkInDate}</span>
                <span className="arrow">→</span>
                <span>{checkOutDate}</span>
              </div>
            )}
          </div>
        </div>

        {error && <div className="error-message">⚠️ {error}</div>}

        {/* Ek Yatak Onay Modal */}
        {showExtraBedWarning && pendingRoom && (
          <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
            <div className="capacity-warning-modal" style={{ background: '#fff', padding: '30px', borderRadius: '12px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
              <h3 style={{ marginTop: 0, color: '#d4af37', fontSize: '1.5rem', marginBottom: '1rem' }}>⚠️ Önemli Bilgilendirme – Ek Yatak</h3>
              
              <div style={{ background: '#f8f9fa', padding: '1.25rem', borderRadius: '8px', marginBottom: '1.5rem', lineHeight: '1.7', fontSize: '0.95rem' }}>
                <p style={{ marginTop: 0, fontWeight: '600', color: '#333' }}>
                  Seçtiğiniz <strong>{pendingRoom.roomTypeName || pendingRoom.name}</strong> odası için <strong>{adultsCount} yetişkin + {childrenCount} çocuk</strong> (toplam {Number(adultsCount) + Number(childrenCount)} kişi) konaklaması talep ettiniz.
                </p>
                
                <p>Bu odanın <strong>standart kapasitesi {pendingRoom.standardCapacity || (pendingRoom.maxOccupancy ? pendingRoom.maxOccupancy - 1 : 3)} kişi</strong> olup, talep ettiğiniz {Number(adultsCount) + Number(childrenCount)} kişilik konaklama için <strong>ek yatak açılması gerekmektedir.</strong></p>
                
                <p style={{ color: '#856404', fontWeight: '600' }}>Ek yatak açıldığında <strong>oda alanı sınırlı</strong> olacağından konfor standart kapasiteye göre daha düşük olabilir.</p>
                
                <hr style={{ margin: '1rem 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
                
                <p style={{ fontWeight: '600', color: '#d4af37', marginBottom: '0.5rem' }}>Rezervasyonunuza devam ederek;</p>
                <ul style={{ paddingLeft: '1.5rem', marginBottom: 0 }}>
                  <li>Odanın standart kapasitesinin <strong>{pendingRoom.standardCapacity || (pendingRoom.maxOccupancy ? pendingRoom.maxOccupancy - 1 : 3)} kişi</strong> olduğunu,</li>
                  <li>Ek yatağın <strong>istisnai olarak açıldığını</strong>,</li>
                  <li><strong>Oda içi konforun sınırlı olabileceğini</strong>,</li>
                  <li>Bu durumun size önceden bildirildiğini</li>
                </ul>
                <p style={{ marginBottom: 0, marginTop: '0.5rem' }}>kabul etmiş olursunuz.</p>
              </div>
              
              <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #ffc107' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404', fontWeight: '500' }}>
                  ⚠️ Ek yatak nedeniyle oluşabilecek konfor beklentileri sebebiyle yapılacak <strong>iptal veya ücret iadesi talepleri kabul edilmemektedir.</strong>
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button 
                  onClick={() => {
                    setShowExtraBedWarning(false);
                    proceedWithBooking(pendingRoom);
                    setPendingRoom(null);
                  }}
                  style={{ flex: 1, padding: '12px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}
                >
                  ✓ Anladım, Devam Et
                </button>
                <button 
                  onClick={() => {
                    setShowExtraBedWarning(false);
                    setPendingRoom(null);
                  }}
                  style={{ flex: 1, padding: '12px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' }}
                >
                  Vazgeç
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- Results Section --- */}
        {searched && !loading && availableRooms.length === 0 && !error && (
          <div className="no-results">
            <p>Aradığınız kriterlere uygun oda bulunamadı.</p>
            <p>Lütfen tarihleri değiştirerek tekrar deneyiniz.</p>
          </div>
        )}

        {searched && availableRooms.length > 0 && (
          <div className="results-section">
            <h2>Müsait Seçenekler</h2>
            <div className="rooms-grid">
              {availableRooms.map(room => {
                const roomId = room.roomTypeId || room.id;
                const currentIndex = currentImageIndex[roomId] || 0;
                const images = room.images || (room.imageUrl ? [room.imageUrl] : []);
                const hasMultipleImages = images.length > 1;
                
                return (
                <div key={roomId} className="room-card">
                  
                  {/* Image Column with Carousel */}
                  <div className="room-image room-image-carousel">
                    {images.length > 0 ? (
                      <>
                        <div 
                          className="room-image-wrapper"
                          onClick={() => openLightbox(roomId)}
                          title="Resmi büyütmek için tıklayın"
                          style={{ cursor: 'pointer', position: 'relative', width: '100%', height: '100%' }}
                        >
                          <LazyImage
                            key={getImageUrl(typeof images[currentIndex] === 'string' ? images[currentIndex] : images[currentIndex]?.url || images[currentIndex])}
                            src={getImageUrl(typeof images[currentIndex] === 'string' ? images[currentIndex] : images[currentIndex]?.url || images[currentIndex])}
                            alt={room.roomTypeName || room.name}
                            wrapStyle={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                          />
                          <div className="zoom-hint">🔍</div>
                        </div>
                        {hasMultipleImages && (
                          <>
                            <button 
                              className="carousel-btn carousel-btn-prev"
                              onClick={(e) => {
                                e.stopPropagation();
                                prevImage(roomId, images.length);
                              }}
                              aria-label="Önceki resim"
                            >
                              ‹
                            </button>
                            <button 
                              className="carousel-btn carousel-btn-next"
                              onClick={(e) => {
                                e.stopPropagation();
                                nextImage(roomId, images.length);
                              }}
                              aria-label="Sonraki resim"
                            >
                              ›
                            </button>
                            <div className="carousel-indicators">
                              {images.map((_: any, index: number) => (
                                <span 
                                  key={index} 
                                  className={`indicator ${index === currentIndex ? 'active' : ''}`}
                                  onClick={() => setCurrentImageIndex(prev => ({ ...prev, [roomId]: index }))}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="no-image">
                        <span>{room.roomTypeName || room.name}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Content Column */}
                  <div className="room-content">
                    <div>
                      <div className="room-header">
                        <h3 className="room-title">{room.roomTypeName || room.name}</h3>
                        <div className="room-meta-badges">
                          <span className="meta-badge">👥 {room.maxAdults} Yetişkin</span>
                          {room.maxChildren > 0 && <span className="meta-badge">👶 {room.maxChildren} Çocuk</span>}
                          {room.viewType && (
                            <span className="meta-badge">
                              {room.viewType === 'sea' ? '🌊 Deniz Manzara' : '🌳 Bahçe Manzara'}
                            </span>
                          )}
                          {/* Ek yatak uyarı badge */}
                          {(Number(adultsCount) + Number(childrenCount)) > (room.standardCapacity || room.maxAdults || room.maxOccupancy || 3) && (
                            <span className="meta-badge" style={{ background: '#ffc107', color: '#856404', fontWeight: '600' }}>
                              ⚠️ Ek yatak gerekli
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {room.description && (
                        <p className="room-desc">{room.description}</p>
                      )}

                      <div className="room-features-list">
                        {getRoomFeatures(room).map((feature: any, idx: number) => (
                          <div key={idx} className="feature-item">
                            {feature}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Pricing/Action Column */}
                  <div className="room-action-area">
                    <div className="price-tag">
                      {room.discountAmount && room.discountAmount > 0 && (
                        <div className="discount-badge-wrapper">
                          <div className="discount-badge">
                            🎉 {room.discountPercentage && `%${room.discountPercentage} `}İndirim
                          </div>
                          {room.discountName && (
                            <div className="discount-info">
                              {room.discountName.replace(' İndirimi', '')}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {room.originalPrice && room.originalPrice > room.price && (
                        <span className="original-price">
                          ₺{room.originalPrice.toLocaleString('tr-TR')}
                        </span>
                      )}
                      
                      <span className="price-label">
                        Yalnızca Oda · {room.nights} Gece
                      </span>
                      <span className="price-amount">
                        <span className="price-currency">₺</span>
                        {room.price ? room.price.toLocaleString('tr-TR') : '0'}
                      </span>
                      
                      {room.discountAmount && room.discountAmount > 0 && (
                        <span className="discount-amount">
                          ₺{room.discountAmount.toLocaleString('tr-TR')} tasarruf
                        </span>
                      )}
                      
                      {/* Günlük Fiyat Detayı */}
                      {room.pricingDetails?.dailyPrices && room.pricingDetails.dailyPrices.length > 1 && (
                        <details className="daily-pricing-details">
                          <summary className="daily-pricing-summary">
                            📋 Günlük Fiyatlar
                          </summary>
                          <div className="daily-pricing-content">
                            {room.pricingDetails.dailyPrices.map((dayPrice: any, idx: number) => (
                              <div key={idx} className="daily-price-item">
                                <span className="daily-date">
                                  {new Date(dayPrice.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                                </span>
                                <span className="daily-amount">
                                  ₺{dayPrice.finalPrice?.toLocaleString('tr-TR')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      
                      <span className="price-period">Vergiler Dahil</span>
                    </div>

                    <div style={{ marginTop: '1.5rem', width: '100%' }}>
                       <button 
                        className="btn-book"
                        onClick={() => handleRoomSelect(room)}
                      >
                        REZERVASYON YAP
                      </button>
                      <p style={{
                        margin: '0.5rem 0 0 0',
                        textAlign: 'center',
                        fontSize: '0.78rem',
                        color: '#aaa',
                        fontStyle: 'italic'
                      }}>
                        Kahvaltı &amp; yemek seçeneği ile devam et →
                      </p>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        )}

        {/* Modal remains mostly standard styling but cleaner */}
        {showBookingForm && selectedRoom && (
          <div className="modal-overlay" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: 'white', padding: '2rem', borderRadius: '12px',
              maxWidth: '500px', width: '90%', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
            }}>
              <h3 style={{ borderBottom: '1px solid #eee', paddingBottom: '1rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
                Rezervasyon Tamamla
              </h3>
              
              <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>{selectedRoom.roomTypeName || selectedRoom.name}</p>
                <p style={{ margin: '0', fontSize: '0.9rem', color: '#666' }}>
                  {checkInDate} — {checkOutDate} ({selectedRoom.nights} Gece)
                </p>
                <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold', fontSize: '1.2rem', color: '#d4af37' }}>
                  {selectedRoom.price?.toLocaleString('tr-TR')} TL
                </p>
              </div>
              
              <form onSubmit={handleBooking} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="form-group">
                  <label>Ad Soyad</label>
                  <input
                    type="text"
                    name="guestName"
                    className="form-input"
                    required
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Örn: Ali Yılmaz"
                  />
                  {guestName.length > 0 && !isValidFullName(guestName) && (
                    <small style={{ color: '#dc3545', marginTop: '0.3rem', display: 'block' }}>
                      Lütfen ad ve soyadınızı birlikte yazın (örn: Ali Yılmaz)
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>E-posta</label>
                  <input type="email" name="guestEmail" className="form-input" required />
                </div>
                <div className="form-group">
                  <label>Telefon</label>
                  <input type="tel" name="guestPhone" className="form-input" required placeholder="+90 555..." />
                </div>

                {/* Misafir Grubu Cinsiyeti */}
                <div className="form-group">
                  <label style={{ fontWeight: '600' }}>
                    Misafir Grubu Cinsiyeti <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <select
                    value={guestGroupType ?? ''}
                    onChange={(e) => setGuestGroupType(e.target.value as '' | 'mixed' | 'male_only')}
                    className="form-input"
                    required
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="">— Lütfen seçiniz —</option>
                    <option value="mixed">Aile / Karma grup (en az bir kadın misafir bulunmaktadır)</option>
                    <option value="male_only">Yalnızca erkek misafir(ler)</option>
                  </select>
                  {guestGroupType === 'male_only' && (
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.85rem 1rem',
                      background: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      color: '#721c24',
                      lineHeight: '1.6'
                    }}>
                      <strong>⛔ Rezervasyon yapılamaz.</strong><br />
                      Villa Polikne, butik bir aile işletmesi konseptiyle hizmet vermektedir. Tesisin genel huzuru ve aile ortamının korunması amacıyla yalnızca erkek misafirlerden oluşan grup veya tekil rezervasyonlar <strong>kabul edilmemektedir</strong>.
                    </div>
                  )}
                </div>

                {/* Meal Add-ons */}
                <div style={{ 
                  background: '#fff8e1', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  border: '1px solid #ffd54f'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#f57c00', fontSize: '1rem' }}>
                    🍽️ Ek Hizmetler
                  </h4>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', padding: '0.5rem', background: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                      <input
                        type="checkbox"
                        checked={includeBreakfast}
                        onChange={(e) => setIncludeBreakfast(e.target.checked)}
                        style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>🍳 Kahvaltı Ekle</div>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                          Konaklama süresince her gün için kahvaltı rezervasyonu
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#f57c00', fontWeight: '600' }}>
                          Yetişkin: ₺{mealPricing.breakfastAdult} | 
                          7-12 yaş: ₺{mealPricing.breakfastChild7_12} | 
                          0-6 yaş: ₺0
                        </div>
                        {includeBreakfast && (
                          <>
                            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👤 Yetişkin:</label>
                                <select 
                                  value={breakfastAdults} 
                                  onChange={(e) => setBreakfastAdults(parseInt(e.target.value))}
                                  style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                                >
                                  {Array.from({length: adultsCount + childrenAges.filter(a => a >= 13).length + 1}, (_, i) => i).map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👶 Çocuk (7-12):</label>
                                <select 
                                  value={breakfastChildren7_12} 
                                  onChange={(e) => setBreakfastChildren7_12(parseInt(e.target.value))}
                                  style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                                >
                                  {Array.from({length: childrenAges.filter(a => a >= 7 && a <= 12).length + 1}, (_, i) => i).map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👶 Çocuk (0-6):</label>
                                <select 
                                  value={breakfastChildren0_6} 
                                  onChange={(e) => setBreakfastChildren0_6(parseInt(e.target.value))}
                                  style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                                >
                                  {Array.from({length: childrenAges.filter(a => a >= 0 && a <= 6).length + 1}, (_, i) => i).map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            {(() => {
                              const mc = calculateMealCosts();
                              return (
                                <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#e8f5e9', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold', color: '#2e7d32' }}>
                                  ☕ {mc.billableBreakfastNights} gece × {breakfastAdults + breakfastChildren7_12 + breakfastChildren0_6} kişi = ₺{mc.breakfast.toLocaleString('tr-TR')}
                                  {mc.excludedBreakfasts > 0 && <div style={{ fontSize:'0.78rem', color:'#e65100', fontWeight:'normal', marginTop:'2px' }}>({mc.excludedBreakfasts} kapalı gün sabahı hariç)</div>}
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', padding: '0.5rem', background: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                      <input
                        type="checkbox"
                        checked={includeDinner}
                        onChange={(e) => setIncludeDinner(e.target.checked)}
                        style={{ marginRight: '0.75rem', marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>🍷 Akşam Yemeği Ekle</div>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
                          Konaklama süresince her gün için akşam yemeği rezervasyonu
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#f57c00', fontWeight: '600' }}>
                          Yetişkin: ₺{mealPricing.dinnerAdult} | 
                          7-12 yaş: ₺{mealPricing.dinnerChild7_12} | 
                          0-6 yaş: ₺{mealPricing.dinnerChild0_6}
                        </div>
                        {includeDinner && (
                          <>
                            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👤 Yetişkin:</label>
                                <select 
                                  value={dinnerAdults} 
                                  onChange={(e) => setDinnerAdults(parseInt(e.target.value))}
                                  style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                                >
                                  {Array.from({length: adultsCount + childrenAges.filter(a => a >= 13).length + 1}, (_, i) => i).map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👶 Çocuk (7-12):</label>
                                <select 
                                  value={dinnerChildren7_12} 
                                  onChange={(e) => setDinnerChildren7_12(parseInt(e.target.value))}
                                  style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                                >
                                  {Array.from({length: childrenAges.filter(a => a >= 7 && a <= 12).length + 1}, (_, i) => i).map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <label style={{ fontSize: '0.85rem', color: '#555', whiteSpace: 'nowrap' }}>👶 Çocuk (0-6):</label>
                                <select 
                                  value={dinnerChildren0_6} 
                                  onChange={(e) => setDinnerChildren0_6(parseInt(e.target.value))}
                                  style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9rem' }}
                                >
                                  {Array.from({length: childrenAges.filter(a => a >= 0 && a <= 6).length + 1}, (_, i) => i).map(n => (
                                    <option key={n} value={n}>{n}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            {(() => {
                              const mc = calculateMealCosts();
                              return (
                                <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#e8f5e9', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 'bold', color: '#2e7d32' }}>
                                  🍽️ {mc.billableDinnerNights} gece × {dinnerAdults + dinnerChildren7_12 + dinnerChildren0_6} kişi = ₺{mc.dinner.toLocaleString('tr-TR')}
                                  {mc.excludedDinners > 0 && <div style={{ fontSize:'0.78rem', color:'#e65100', fontWeight:'normal', marginTop:'2px' }}>({mc.excludedDinners} kapalı gün gecesi hariç)</div>}
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                    </label>
                  </div>

                  {/* Pazartesi / Pazar uyarı onay kutuları */}
                  {(() => {
                    const excl = getMondayExclusions(checkInDate, checkOutDate);
                    const showSunday = excl.breakfasts > 0;
                    const showMonday = excl.dinners > 0;
                    if (!showSunday && !showMonday) return null;
                    return (
                      <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {showSunday && (
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.75rem', background: '#fff3e0', borderRadius: '6px', border: '1px solid #ff9800', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={sundayMondayAgreed}
                              onChange={e => setSundayMondayAgreed(e.target.checked)}
                              style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '0.82rem', color: '#e65100', lineHeight: '1.5' }}>
                              Bazı günlerde tesisimizde kahvaltı ve akşam yemeği hizmeti verilmediğini, bu günlere denk gelen konaklama gecelerinde ücretlendirme dışı bırakıldığını okudum, anladım ve kabul ediyorum.
                            </span>
                          </label>
                        )}
                        {showMonday && (
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.75rem', background: '#fce4ec', borderRadius: '6px', border: '1px solid #e91e63', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={mondayAgreed}
                              onChange={e => setMondayAgreed(e.target.checked)}
                              style={{ marginTop: '3px', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '0.82rem', color: '#880e4f', lineHeight: '1.5' }}>
                              <strong>Kapalı günlerde tesisimizde akşam yemeği ve bazı hizmetlerin sunulmadığını</strong>, bu nedenle kapalı günlere denk gelen konaklamaların buna göre fiyatlandırıldığını okudum ve kabul ediyorum.
                            </span>
                          </label>
                        )}
                      </div>
                    );
                  })()}
                </div>
                
                <div className="form-group">
                  <label>İndirim Kodu (Opsiyonel)</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input 
                      type="text" 
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      className="form-input" 
                      placeholder="İndirim kodunuz varsa girin"
                      disabled={!!discountApplied}
                      style={{ flex: 1 }}
                    />
                    {!discountApplied ? (
                      <button 
                        type="button"
                        onClick={handleApplyDiscount}
                        disabled={discountLoading || !discountCode.trim()}
                        style={{
                          padding: '0.5rem 1rem',
                          background: discountLoading ? '#ccc' : '#d4af37',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: discountLoading ? 'not-allowed' : 'pointer',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {discountLoading ? 'Kontrol...' : 'Uygula'}
                      </button>
                    ) : (
                      <button 
                        type="button"
                        onClick={handleRemoveDiscount}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        Kaldır
                      </button>
                    )}
                  </div>
                  {discountMessage.text && (
                    <small style={{ 
                      display: 'block',
                      marginTop: '0.5rem',
                      color: discountMessage.type === 'success' ? '#27ae60' : 
                             discountMessage.type === 'error' ? '#e74c3c' : '#f39c12'
                    }}>
                      {discountMessage.text}
                    </small>
                  )}
                </div>
                
                {/* Fiyat Özeti */}
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <span>Konaklama ({selectedRoom.nights} gece):</span>
                    <span style={{ fontWeight: 'bold' }}>
                      {selectedRoom.price?.toLocaleString('tr-TR')} TL
                    </span>
                  </div>
                  {discountApplied && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      flexWrap: 'wrap',
                      color: '#27ae60'
                    }}>
                      <span>İndirim ({discountApplied.code}):</span>
                      <span style={{ fontWeight: 'bold' }}>
                        -{discountApplied.discount_amount.toFixed(2)} TL
                      </span>
                    </div>
                  )}
                  {includeBreakfast && calculateMealCosts().breakfast > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      flexWrap: 'wrap',
                      color: '#f57c00'
                    }}>
                      <span>🍳 Kahvaltı ({calculateMealCosts().billableBreakfastNights} gün{calculateMealCosts().excludedBreakfasts > 0 ? ', Pzt hariç' : ''}):</span>
                      <span style={{ fontWeight: 'bold' }}>
                        +{calculateMealCosts().breakfast.toLocaleString('tr-TR')} TL
                      </span>
                    </div>
                  )}
                  {includeDinner && calculateMealCosts().dinner > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      flexWrap: 'wrap',
                      color: '#f57c00'
                    }}>
                      <span>🍷 Akşam Yemeği ({calculateMealCosts().billableDinnerNights} gün{calculateMealCosts().excludedDinners > 0 ? ', kapalı günler hariç' : ''}):</span>
                      <span style={{ fontWeight: 'bold' }}>
                        +{calculateMealCosts().dinner.toLocaleString('tr-TR')} TL
                      </span>
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    paddingTop: '0.5rem',
                    borderTop: '2px solid #dee2e6',
                    fontSize: '1.2rem'
                  }}>
                    <span style={{ fontWeight: 'bold' }}>Toplam:</span>
                    <span style={{ fontWeight: 'bold', color: '#d4af37' }}>
                      {(() => {
                        const roomPrice = discountApplied ? discountApplied.final_amount : (selectedRoom.price || 0);
                        const totalWithMeals = roomPrice + calculateMealCosts().total;
                        return totalWithMeals.toLocaleString('tr-TR');
                      })()} TL
                    </span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Notlar</label>
                  <textarea name="specialRequests" className="form-input" rows={3}></textarea>
                </div>

                {/* Fatura Bilgileri */}
                <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.75rem', fontSize: '0.95rem', color: '#2c3e50' }}>🧾 Fatura Bilgileri (Zorunlu)</div>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    {(['individual', 'corporate'] as const).map((type) => (
                      <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input
                          type="radio"
                          name="invoice_type_radio"
                          value={type}
                          checked={invoiceType === type}
                          onChange={() => setInvoiceType(type)}
                        />
                        {type === 'individual' ? 'Bireysel' : 'Kurumsal'}
                      </label>
                    ))}
                  </div>
                  {invoiceType === 'individual' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      <div>
                        <input type="text" className="form-input" placeholder="Ad Soyad (örn: Ali Yılmaz)" value={invoiceName} onChange={(e) => setInvoiceName(e.target.value)} required />
                        {invoiceName.length > 0 && !isValidFullName(invoiceName) && (
                          <small style={{ color: '#dc3545', marginTop: '0.3rem', display: 'block' }}>
                            Lütfen ad ve soyadınızı birlikte yazın (örn: Ali Yılmaz)
                          </small>
                        )}
                      </div>
                      <input type="text" className="form-input" placeholder="T.C. Kimlik No (isteğe bağlı)" value={invoiceTckn} onChange={(e) => setInvoiceTckn(e.target.value.replace(/\D/g, '').slice(0, 11))} maxLength={11} />
                      <input type="text" className="form-input" placeholder="Adres (Mahalle, Cadde, No)" value={invoiceAddress} onChange={(e) => setInvoiceAddress(e.target.value)} required />
                      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <input type="text" className="form-input" placeholder="İlçe" value={invoiceDistrict} onChange={(e) => setInvoiceDistrict(e.target.value)} style={{ flex: '1 1 100px', minWidth: '80px' }} required />
                        <input type="text" className="form-input" placeholder="İl" value={invoiceCity} onChange={(e) => setInvoiceCity(e.target.value)} style={{ flex: '1 1 100px', minWidth: '80px' }} required />
                        <input type="text" className="form-input" placeholder="Posta Kodu" value={invoicePostalCode} onChange={(e) => setInvoicePostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))} maxLength={5} style={{ flex: '1 1 80px', minWidth: '80px' }} />
                      </div>
                    </div>
                  )}
                  {invoiceType === 'corporate' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      <input type="text" className="form-input" placeholder="Firma Ünvanı" value={invoiceName} onChange={(e) => setInvoiceName(e.target.value)} required />
                      <input type="text" className="form-input" placeholder="Vergi Numarası (VKN)" value={invoiceTaxNumber} onChange={(e) => setInvoiceTaxNumber(e.target.value.replace(/\D/g, '').slice(0, 10))} maxLength={10} required />
                      <input type="text" className="form-input" placeholder="Vergi Dairesi" value={invoiceTaxOffice} onChange={(e) => setInvoiceTaxOffice(e.target.value)} required />
                      <input type="text" className="form-input" placeholder="Adres (Mahalle, Cadde, No)" value={invoiceAddress} onChange={(e) => setInvoiceAddress(e.target.value)} required />
                      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <input type="text" className="form-input" placeholder="İlçe" value={invoiceDistrict} onChange={(e) => setInvoiceDistrict(e.target.value)} style={{ flex: '1 1 100px', minWidth: '80px' }} required />
                        <input type="text" className="form-input" placeholder="İl" value={invoiceCity} onChange={(e) => setInvoiceCity(e.target.value)} style={{ flex: '1 1 100px', minWidth: '80px' }} required />
                        <input type="text" className="form-input" placeholder="Posta Kodu" value={invoicePostalCode} onChange={(e) => setInvoicePostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))} maxLength={5} style={{ flex: '1 1 80px', minWidth: '80px' }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Satış Sözleşmesi */}
                <div style={{ marginTop: '1rem' }}>
                  <SalesAgreement
                    isChecked={agreementAccepted}
                    onCheckChange={setAgreementAccepted}
                  />
                </div>
                
                {/* Fiyat Yükleme Hatası */}
                {mealPriceError && (
                  <div style={{ 
                    padding: '1rem', 
                    background: '#fff3cd', 
                    border: '2px solid #ff9800',
                    borderRadius: '8px',
                    color: '#856404',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    ⚠️ Fiyat bilgileri yüklenemedi. Rezervasyon şu anda yapılamıyor. 
                    Lütfen sayfayı yenileyip tekrar deneyin veya iletişime geçin.
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <button 
                    type="submit" 
                    className="btn-book" 
                    style={{ flex: 2 }}
                    disabled={(() => {
                      if (mealPriceError) return true;
                      if (guestGroupType === 'male_only') return true;
                      if (!isValidFullName(guestName)) return true;
                      if (invoiceType === 'individual' && !isValidFullName(invoiceName)) return true;
                      if (!agreementAccepted) return true;
                      const excl = getMondayExclusions(checkInDate, checkOutDate);
                      if (excl.breakfasts > 0 && !sundayMondayAgreed) return true;
                      if (excl.dinners > 0 && !mondayAgreed) return true;
                      return false;
                    })()}
                  >
                    ONAYLA VE ÖDE
                  </button>
                  <button 
                    type="button" 
                    onClick={() => { setShowBookingForm(false); setGuestName(''); setIncludeBreakfast(false); setIncludeDinner(false); setBreakfastAdults(0); setBreakfastChildren7_12(0); setBreakfastChildren0_6(0); setDinnerAdults(0); setDinnerChildren7_12(0); setDinnerChildren0_6(0); }}
                    style={{ flex: 1, border: 'none', background: 'transparent', color: '#888', cursor: 'pointer' }}
                  >
                    Vazgeç
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <PaymentModal 
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          iframeUrl={paymentUrl}
          bookingId={createdBookingId}
          skipAgreement={true}
        />

        {/* Lightbox for Image Zoom */}
        {lightboxOpen && activeRoomId && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <div className="lightbox-content" onClick={e => e.stopPropagation()}>
              <button className="lightbox-close" onClick={closeLightbox}>×</button>
              {(() => {
                const activeRoom = availableRooms.find(r => (r.roomTypeId || r.id) === activeRoomId);
                if (!activeRoom) return null;
                
                const images = activeRoom.images || (activeRoom.imageUrl ? [activeRoom.imageUrl] : []);
                if (images.length === 0) return null;
                
                const currentIndex = currentImageIndex[activeRoomId] || 0;
                const currentImage = images[currentIndex];
                const imageUrl = typeof currentImage === 'string' ? currentImage : currentImage?.url || currentImage;
                
                return (
                  <>
                    <div className="lightbox-image-container">
                      <img 
                        key={getImageUrl(imageUrl)}
                        src={getImageUrl(imageUrl)} 
                        alt={activeRoom.roomTypeName || activeRoom.name} 
                        className="lightbox-image"
                        style={{ animation: 'fadeIn 0.35s ease' }}
                      />
                    </div>
                    {images.length > 1 && (
                      <>
                        <button 
                          className="lightbox-nav lightbox-prev"
                          onClick={() => prevImage(activeRoomId, images.length)}
                        >‹</button>
                        <button 
                          className="lightbox-nav lightbox-next"
                          onClick={() => nextImage(activeRoomId, images.length)}
                        >›</button>
                        <div className="lightbox-caption">
                          {currentIndex + 1} / {images.length}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationPage;
